import { Network, AlertOctagon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const eur = (v) => (v == null ? null : `${Math.round(v / 1000).toLocaleString("fr-FR")} k€`);

function Encart({ icon: Icon, title, tone, testId, children }) {
    const tones = { alert: "border-rose-200 bg-rose-50/60", info: "border-primary/20 bg-accent/40", ok: "border-emerald-200 bg-emerald-50/40" };
    const iconTone = { alert: "text-rose-700", info: "text-primary", ok: "text-emerald-700" };
    return (
        <Card data-testid={testId} className={`shadow-sm ${tones[tone]}`}>
            <CardContent className="pt-5 pb-5 space-y-2">
                <p className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] ${iconTone[tone]}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.75} /> {title}
                </p>
                <div className="text-sm leading-relaxed text-foreground">{children}</div>
            </CardContent>
        </Card>
    );
}

export default function GroupeSynthese({ nomEntreprise, groupe: g, checks }) {
    if (!g) return null;
    const mere = g.societe_mere;
    const ca = eur(g.ca_total);
    const perimetre = `${g.nb_societes} société${g.nb_societes > 1 ? "s" : ""} (${g.nb_actives} active${g.nb_actives > 1 ? "s" : ""})`;

    const fromChecks = Object.entries(checks || {})
        .filter(([, c]) => c && !c.loading && !c.error && (c.procedure_en_cours || c.procedures?.length > 0))
        .map(([siren, c]) => ({ siren, nom_entreprise: c.nom_entreprise || `SIREN ${siren}`, en_cours: !!c.procedure_en_cours, nb_procedures: c.procedures?.length || 0 }));
    const difficultes = [...(g.difficultes || [])];
    for (const d of fromChecks) if (!difficultes.some((x) => x.siren === d.siren)) difficultes.push(d);
    const autres = difficultes.filter((d) => !d.auditee);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Encart icon={Network} title="Appartenance groupe" tone="info" testId="encart-groupe">
                {mere ? (
                    <p data-testid="encart-groupe-texte">
                        <strong>{nomEntreprise}</strong> appartient à un groupe dont la société mère probable est <strong>{mere.nom_entreprise}</strong>
                        {eur(mere.chiffre_affaires) ? ` (CA ${eur(mere.chiffre_affaires)})` : ""}. Le périmètre reconstitué compte {perimetre}
                        {ca ? <> pour un <strong>chiffre d&apos;affaires global cumulé de {ca}</strong> (dernier exercice connu, {g.ca_connu_sur} société{g.ca_connu_sur > 1 ? "s" : ""} renseignée{g.ca_connu_sur > 1 ? "s" : ""}).</> : " ; aucun chiffre d'affaires publié sur le périmètre."}
                    </p>
                ) : (
                    <p data-testid="encart-groupe-texte">
                        Aucune holding identifiée pour <strong>{nomEntreprise}</strong> (NAF 6420Z/6430Z/7010Z ou dénomination « holding »). Le dirigeant pilote néanmoins un périmètre de {perimetre}
                        {ca ? <> représentant un <strong>chiffre d&apos;affaires global cumulé de {ca}</strong> (dernier exercice connu, {g.ca_connu_sur} société{g.ca_connu_sur > 1 ? "s" : ""} renseignée{g.ca_connu_sur > 1 ? "s" : ""}).</> : "."}
                    </p>
                )}
            </Encart>
            <Encart icon={AlertOctagon} title="Synthèse des difficultés" tone={autres.length ? "alert" : "ok"} testId="encart-difficultes">
                {autres.length === 0 ? (
                    <p data-testid="encart-difficultes-texte">
                        Aucune autre société gérée par le dirigeant n&apos;apparaît en procédure collective dans les données Pappers
                        {g.nb_societes > 1 ? " (utilisez « Vérifier » dans le tableau ci-dessous pour contrôler chaque mandat)" : ""}.
                    </p>
                ) : (
                    <>
                        <p data-testid="encart-difficultes-texte">
                            <strong>{autres.length}</strong> autre{autres.length > 1 ? "s" : ""} société{autres.length > 1 ? "s" : ""} gérée{autres.length > 1 ? "s" : ""} par le dirigeant {autres.length > 1 ? "sont ou ont été" : "est ou a été"} en procédure collective :
                        </p>
                        <ul className="mt-1 space-y-1">
                            {autres.map((d) => (
                                <li key={d.siren} data-testid={`difficulte-${d.siren}`} className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-2 rounded-full bg-rose-600" />
                                    <strong>{d.nom_entreprise}</strong>
                                    <span className="text-muted-foreground">— {d.en_cours ? "procédure en cours" : `${d.nb_procedures || 1} procédure${d.nb_procedures > 1 ? "s" : ""} passée${d.nb_procedures > 1 ? "s" : ""}`}{d.cessee ? " · société cessée" : ""}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </Encart>
        </div>
    );
}
